import { FC, ReactNode, MouseEvent, useCallback } from 'react';
import { css, cx } from '@emotion/css';
import Link from 'next/link';

import { make as Tag } from '@prezzee/ribbon/src/components/common/Tag/Tag.gen';

import { useLayoutData } from 'contexts/LayoutDataContext';
import { ImageConfig } from 'components/StaticImage';
import { make as CardZoomAnimation } from 'components/CardZoomAnimation.bs';
import { rootContainer, cardImg, cardName, cardPrice } from 'components/library/CardStyles.bs';
import { Color } from 'css/theme';
import { Card_t } from 'models/Models.gen';
import { t as SkuPresentation_t } from 'api/graphql/SkuPresentation/Gql__SkuPresentation__Fragment__Decoder.gen';
import {
  cardToProductWithIndex as Segment_cardToProductWithIndex,
  productClicked as Segment_productClicked,
} from 'bindings/Segment.bs';
import { EMPTY_STRING } from 'common/utils/string';
import CmsImage from 'components/CmsImage';
import { EMPTY_ARRAY } from 'common/utils/array';

type CardContentProps = {
  children: ReactNode;
  onClick?: (e: MouseEvent) => void;
  card: Card_t;
  position: number;
};

type CardProps = {
  card: Card_t;
  inGrid?: boolean;
  position: number;
  onClick?: (e: MouseEvent) => void;
  skuDict?: [string, SkuPresentation_t][];
};

const style = {
  soldOutLabel: css`
    position: absolute;
    right: 0;
    top: 0;
    border-bottom-left-radius: 8px;
    border-top-right-radius: 8px;
    background-color: ${Color.Red50};
    color: #ed0b09;
    z-index: 1;
    text-align: center;
    padding: 2px 10px 4px;
    font-size: 14px;
    line-height: 22px;
    min-width: 40%;
  `,
  cardZoomAnimation: (isSoldOut: boolean) => css`
    position: ${isSoldOut ? 'relative' : 'static'};
  `,
  tagline: css`
    margin: 0 12px 12px;
    @media (min-width: 768px) {
      margin-left: 16px;
      margin-right: 16px;
    }
  `,
  innerContainer: (isSoldOut: boolean) => css`
    opacity: ${isSoldOut ? 0.4 : 1.0};
    position: relative;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(209, 213, 223, 50%);
    background-color: ${Color.White};
    overflow-y: hidden;
    min-height: 177px;

    @media (min-width: 410px) and (max-width: 624px) {
      min-height: 225px;
    }

    @media (min-width: 625px) and (max-width: 752px) {
      min-height: 284px;
    }

    @media (min-width: 753px) and (max-width: 1439px) {
      min-height: 247px;
    }

    @media (min-width: 1440px) {
      min-height: 257px;
    }

    & > a {
      cursor: pointer;
    }
  `,
};

const PROMO_ICON: ImageConfig = {
  src: require('../../../public/images/icon_promo.svg'),
  alt: 'Promo icon',
  width: 30,
  height: 79,
};

const desktopMd = '(max-width: 1440px)';
const desktopSm = '(max-width: 1225px)';
const tablet = '(max-width: 768px)';
const mobileLg = '(max-width: 640px)';

const CardContent: FC<CardContentProps> = ({ children, onClick, card, position }) => {
  if (onClick) {
    return <a onClick={onClick}>{children}</a>;
  }

  return (
    <Link href={`/store/${card.slug}`} prefetch={false}>
      <a
        onClick={() => {
          Segment_productClicked(Segment_cardToProductWithIndex(position, card));
        }}
      >
        {children}
      </a>
    </Link>
  );
};

const Card: FC<CardProps> = ({ skuDict, card, position, onClick, inGrid = true }) => {
  const layoutData = useLayoutData();
  const soldOutBadgeText = layoutData?.soldOutBadgeText || EMPTY_STRING;

  // NOTE: The check is required for type compatibility with Rescript
  const skuDictArray = Array.isArray(skuDict) ? skuDict : EMPTY_ARRAY;
  const hasPromos = Boolean(card.promos.length);
  const skuData = Object.fromEntries(skuDictArray)[card.cmsId];
  const isSoldOut = skuData?.soldOut || false;
  const imageUrl = skuData ? skuData.themes[0]?.image.url || card.imgUrl : card.imgUrl;
  const soldOutLabel = <div className={style.soldOutLabel}>{soldOutBadgeText}</div>;

  const onClickHandler = useCallback(() => {
    if (isSoldOut) {
      return (e: MouseEvent) => e.preventDefault();
    }

    return onClick;
  }, [isSoldOut]);

  const applyGridStyles = (className: string[]) => {
    if (inGrid) {
      return cx(...className);
    }

    return EMPTY_STRING;
  };

  return (
    <div className={applyGridStyles([rootContainer])}>
      <CardZoomAnimation className={style.cardZoomAnimation(isSoldOut)}>
        {isSoldOut ? soldOutLabel : null}
        <div className={style.innerContainer(isSoldOut)}>
          {hasPromos ? (
            <CmsImage
              className="absolute z-10 top-0 left-0"
              alt={PROMO_ICON.alt}
              src={PROMO_ICON.src}
              width={PROMO_ICON.width}
              height={PROMO_ICON.height}
            />
          ) : null}
          <CardContent card={card} position={position} onClick={onClickHandler()}>
            <div className="block">
              <div className="ratio-box fade-box">
                <CmsImage
                  alt={card.name}
                  src={imageUrl}
                  className={cardImg}
                  width={256}
                  height={159}
                  sizes={`${mobileLg} 47vw, ${tablet} 304px, ${desktopSm} 240px, ${desktopMd} 224px, 256px`}
                />
              </div>
            </div>
            <h3
              className={applyGridStyles([
                cardName,
                'font-semibold text-sm leading-4 sm:text-17px sm:leading-5 px-3 md:px-4 my-2',
              ])}
            >
              {card.isSwappable ? card.displayName : card.name}
            </h3>
            {card.tagline ? (
              // @ts-ignore
              <Tag className={style.tagline}>{card.tagline}</Tag>
            ) : (
              <p
                className={applyGridStyles([cardPrice, 'text-mid-gray px-3 md:px-4 text-xs sm:text-sm leading-4 pb-3'])}
              >
                {card.priceRange}
              </p>
            )}
          </CardContent>
        </div>
      </CardZoomAnimation>
    </div>
  );
};

export default Card;
